import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios, { AxiosRequestConfig } from 'axios';
import { serializeAxiosError } from "../../../../Config/Reducers/ReducerUtils";

export interface TrackerAvailability  {
    trackerCode: string;
    alarmCount: number;
    durationHours: number;
    periodStart: string;
    periodEnd: string;
}

export interface TrackerAvailabilityRequest {
    powerPlantId: number;
    startDateTime: string;
    endDateTime: string;
}

export interface TrackerAvailabilityState {
    trackerAvailability: TrackerAvailability[];
    loading: boolean,
    errorMessage: string | null | undefined,
    errorStatus: number | null,
}

interface TrackerAvailabilityError {
    message: string;
    status: number | null;
}

const initialState: TrackerAvailabilityState = {
    trackerAvailability: [],
    loading: false,
    errorMessage: null,
    errorStatus: null
};

const apiUrl = '/api/power-station/tracker-availability';

const getTrackerAvailabilityError = (error: any): TrackerAvailabilityError => {
    const status = error?.response?.status ?? null;
    const responseData = error?.response?.data;
    const message = responseData?.detail
        ?? responseData?.message
        ?? error?.message
        ?? 'Não foi possível carregar os dados.';

    return {message, status};
};

// Actions
export const getTrackerAvailability = createAsyncThunk<TrackerAvailability[], TrackerAvailabilityRequest, {rejectValue: TrackerAvailabilityError }>(
    'dashboard_power_plant/tracker/availability',
    async ({powerPlantId, startDateTime, endDateTime}, {rejectWithValue}) => {
        const config: AxiosRequestConfig = {
            url: `${apiUrl}/${powerPlantId}`,
            params: {
                startDateTime,
                endDateTime,
            },
            method: 'GET'
        };

        try {
            const response = await axios(config);
            return response.data.data;
        } catch (error) {
            return rejectWithValue(getTrackerAvailabilityError(error));
        }
    },  
    { serializeError: serializeAxiosError }
);

// Slices
const TrackerAvailabilityReducer = createSlice({
    name: 'countries',
    initialState,
    reducers: {
        /**
         * Reset the entity state to initial state
         */
        reset() {
            return initialState;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getTrackerAvailability.pending, (state) => {
                state.loading = true;
                state.errorMessage = null;
                state.errorStatus = null;
            })
            .addCase(getTrackerAvailability.fulfilled, (state, action) => {
                state.trackerAvailability = action.payload;
                state.loading = false;
                state.errorMessage = null;
                state.errorStatus = null;
            })
            .addCase(getTrackerAvailability.rejected, (state, action) => {
                state.trackerAvailability = [];
                state.loading = false;
                state.errorMessage = action.payload?.message ?? action.error.message ?? 'Não foi possível carregar os dados.';
                state.errorStatus = action.payload?.status ?? null;
            })
    },
})

export const { reset } = TrackerAvailabilityReducer.actions;

// Reducer
export default TrackerAvailabilityReducer.reducer;
